import { Fragment } from "react";

import {
  BreadcrumbButton,
  BreadcrumbCurrent,
  BreadcrumbLink,
  BreadcrumbNav,
  BreadcrumbSeparator,
} from "./AdminBreadcrumb.styles";

export interface IAdminBreadcrumbItem {
  label: string;
  /** Route this step goes back to. */
  to?: string;
  /** In-view navigation instead of a route (e.g. a drill-down's levels). */
  onClick?: () => void;
}

interface IAdminBreadcrumbProps {
  /** Ancestors first, current page last. */
  items: IAdminBreadcrumbItem[];
  /** Overrides the landmark label when a page carries more than one trail. */
  ariaLabel?: string;
}

/**
 * Trail across an /admin page's ancestors. Detail pages use it instead of a
 * Back button: it says where you are as well as where you'd return to, and it
 * scales to the deeper pages still to migrate (Hospital Details → ward →
 * staff) where a single Back button can't express the path.
 *
 * The last item is always the current page and is never a link. Steps with
 * `to` are route links; steps with `onClick` navigate within the view, which
 * is what a drill-down inside one modal needs.
 */
export function AdminBreadcrumb({
  items,
  ariaLabel = "Breadcrumb",
}: IAdminBreadcrumbProps) {
  return (
    <BreadcrumbNav aria-label={ariaLabel}>
      {items.map((item, index) => {
        const isCurrent = index === items.length - 1;

        return (
          <Fragment key={`${item.label}-${index.toString()}`}>
            {index > 0 && (
              <BreadcrumbSeparator aria-hidden="true">/</BreadcrumbSeparator>
            )}
            {isCurrent && (
              <BreadcrumbCurrent aria-current="page">
                {item.label}
              </BreadcrumbCurrent>
            )}
            {!isCurrent && item.to && (
              <BreadcrumbLink to={item.to}>{item.label}</BreadcrumbLink>
            )}
            {!isCurrent && !item.to && (
              <BreadcrumbButton type="button" onClick={item.onClick}>
                {item.label}
              </BreadcrumbButton>
            )}
          </Fragment>
        );
      })}
    </BreadcrumbNav>
  );
}
